import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY, DEFAULT_SEARCH_INPUT_LENGTH } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { TPersonalDetailsFormValues } from "../../../models/Onboarding/onboarding";
import { WORKING_RIGHTS_OPTIONS } from "../../../models/Onboarding/onboarding-values";
import Roles from "../../../models/roles";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { CandidateStorage } from "../../../services/CandidateStorage";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getAddressSuggestionsThunk } from "../../../store/auth/thunks";
import { AddressSuggestion } from "../../../store/auth/types";
import { personalDetailsThunk } from "../../../store/onboarding/thunk";
import { getCandidateData, profileEditRequestStatusThunk } from "../../../store/superadmin/thunks";
import { TFileUploadsResponse } from "../../../store/uploads/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import CustomButton from "../../UI/Buttons/CustomButton";
import RightArrow from "../../UI/Icons/RightArrow";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import Checkbox from "../../UI/Inputs/Checkbox";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import ImageUpload from "../../UI/Inputs/ImageUpload";
import RadioInput from "../../UI/Inputs/RadioInput";
import TextInput from "../../UI/Inputs/TextInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

const PersonalDetails: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  /** Get the value from state */
  const error = useSelector((state) => state.auth.error);
  const user = useSelector((state) => state.auth.user);
  const profileData = useSelector((state) => state.onboarding.personaldetails);
  const {isImpersonating} = useImpersonation();

  // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
  const isSuperAdmin =
    (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
    (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  const userpersonaldetails = useSelector(
    (state) =>
      !isSuperAdmin ? state.onboarding.userpersonaldetails :
      state.superadmin.reviewCandidateData
  );
  const personaldetails = useSelector(
    (state) =>
      !isSuperAdmin ? state.onboarding.personaldetails :
      state.superadmin.reviewCandidateData?.profile
  );
  const isConnectIdVerified = useSelector(
    (state) =>
      !isSuperAdmin ? state.onboarding.isConnectIdVerified :
      state.superadmin.reviewCandidateData?.is_connectid_verified
  );
  const addressSuggestionArr = useSelector(
    (state) => state.auth.addressSuggestions
  );

  useEffect(() => {
    if (addressSuggestionArr.length > 0) {
      setAddressSuggestions(addressSuggestionArr);
    }
  }, [addressSuggestionArr]);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null);
  const [checkPath, setCheckPath] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false)
  const [selectPermanentEmployment, setSelectPermanentEmployment] = useState<boolean>(personaldetails?.permanent_employment ?? false)

  const phone_number = sessionStorage.getItem("phone_number");
  const address = sessionStorage.getItem("address");
  const working_rights = sessionStorage.getItem("working_rights");

  /** Set the value for working rights */
  const handleWorkingRightsOpt = (value: string) => {
    setFieldValue("working_rights", value);
  };

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch)
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   if(selectedKeyword !== ""){
  //     const handleAddressSuggestions = async(selectedKeyword: string) => {
  //       await dispatch(getAddressSuggestionsThunk(selectedKeyword));
  //     }

  //     handleAddressSuggestions(selectedKeyword)
  //   } else {
  //     dispatch(resetAddressSuggestionsListAction());
  //   }
  // }, [dispatch, selectedKeyword])

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(getAddressSuggestionsThunk(selectedKeyword));
    
    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else if (searchInput.length === 0) {
      setAddressSuggestions([]);
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setHasAddressSelected(false)
    setFieldValue("address", inputValue);
    if(inputValue.length > 2 && !hasAddressSelected){
      handleAddressSuggestions(inputValue);
    }
  };

  const handleSelectAddress = (selectedAddress: string) => {
    setFieldValue("address", selectedAddress);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("")
    setHasAddressSelected(true)
  };

  /** Update the value for resume and resume name */
  const handleFileSelect = (file: TFileUploadsResponse, fileName: string) => {
    setFieldValue("resume", file.data.resume);
    setFieldValue("resume_name", fileName);
  };

  /** Submit the form for personal-details */
  const { handleSubmit, values, errors, touched, setFieldValue, handleChange } =
    useFormik({
      validationSchema: createValidationSchema("personalDetails"),
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        first_name:
          userpersonaldetails !== null ? userpersonaldetails.first_name : "",
        last_name:
          userpersonaldetails !== null ? userpersonaldetails.last_name : "",
        phone_number:
          userpersonaldetails !== null &&
          userpersonaldetails.phone_number !== null
            ? userpersonaldetails.phone_number
            : phone_number
            ? phone_number
            : "",
        address:
          userpersonaldetails !== null && userpersonaldetails.address !== null
            ? userpersonaldetails.address
            : address
            ? address
            : "",
        photo: isSuperAdmin
          ? userpersonaldetails?.photo ?? ""
          : user?.photo ?? "",
        resume: "",
        resume_name: "",
        working_rights:
          personaldetails !== null && personaldetails?.working_rights
            ? personaldetails.working_rights
            : working_rights
            ? working_rights
            : "",
        permanent_employment: personaldetails?.permanent_employment ? personaldetails?.permanent_employment : false
      },
      onSubmit: async (values: TPersonalDetailsFormValues) => {
        dispatch(showTransparentPreloaderAction());
        const payload = { ...values };
        // Only set when a Super‑Admin edits a candidate profile; required by the API to identify the candidate.
        if (
          isSuperAdmin &&
          userpersonaldetails &&
          "uuid" in userpersonaldetails
        ) {
          payload.candidate_uuid = userpersonaldetails.uuid;
          payload.isSuperAdmin = isSuperAdmin;
        }

        try {
          const result = await dispatch(personalDetailsThunk(payload));
          if (isFulfilled(result)) {
            if (isSuperAdmin) {
              /** read the request_id and request_from that was persisted in localStorage */
              const storedRequestId = localStorage.getItem("request_id");
              const storedRequestFrom = localStorage.getItem("request_from");
              // if (storedRequestId && storedRequestFrom) {
                if(storedRequestId !== null && storedRequestFrom !== null) {
                  await dispatch(
                    profileEditRequestStatusThunk({
                      request_id: storedRequestId,
                      request_from: storedRequestFrom,
                    })
                  );
                }
                // const storedId = CandidateStorage.get();
                // if (storedId !== null) {
                //   await dispatch(getCandidateData({ candidate_id: storedId }));
                // }
                // if(isFulfilled(editResponse)){
                  const candidateResponse = await dispatch(
                    getCandidateData({ candidate_id: userpersonaldetails?.uuid })
                  );
                  // if(isFulfilled(candidateResponse)) {
                  //   dispatch(showNotificationAction("Updated"))
                  // }
                // }

                CandidateStorage.save(userpersonaldetails?.id ?? 0);
              // }
              dispatch(showNotificationAction("Updated"))
            } else {
              if (checkPath) {
                navigate("/candidate/my-profile/qualifications");
              } else {
                navigate(AUTH_ROUTE.QUALIFICATIONS);
              }
            }
          }
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
        dispatch(hideTransparentPreloaderAction());
      },
    });

  /** Fetch the value from api and set the same for the fields */
  useEffect(() => {
    if (user !== null) {
      setFieldValue(
        "photo",
        isSuperAdmin ? userpersonaldetails?.photo : user?.photo
      );
    }

    if (personaldetails !== null) {
      setFieldValue("resume", personaldetails?. resume);
      setFieldValue("resume_name", personaldetails?.resume_name);
    }

    setFieldValue("permanent_employment", selectPermanentEmployment);
  }, [setFieldValue, user, personaldetails, selectPermanentEmployment, isSuperAdmin, userpersonaldetails]);

  /** Check all the values got updated for enabling the next button */
  useEffect(() => {
    if (
      values.first_name !== "" &&
      values.last_name !== "" &&
      values.phone_number !== "" &&
      values.address !== "" &&
      values.photo !== null &&
      values.working_rights !== "" &&
      values.working_rights !== null &&
      values.working_rights !== "dont_have_australia_working_rights"
    ) {
      setIsDisabled(!true);
    } else {
      setIsDisabled(true);
    }

    sessionStorage.setItem("working_rights", values.working_rights);
  }, [values]);

  useEffect(() => {
    if (location.pathname.includes("candidate")) {
      setCheckPath(true);
    }
  }, [location]);

  const handlePermenentEmploymentCheckBox = (checked: boolean) => {
    setSelectPermanentEmployment(checked ? true : false)
  }

  // Get the review status for the current profile
  const reviewStatus = profileData?.review_status;

  /**
   * Determines if a field should be disabled based on status and user role
   * - Super Admin during impersonation: Can ALWAYS edit (any status)
   * - Candidate with "approved" status: CANNOT edit (completed and approved)
   * - Candidate with "waiting" status: CAN edit (submitted for review)
   * - Candidate with null status: CAN edit (not yet submitted)
   */
  const shouldFieldBeDisabled = () => {
    // Super Admin during impersonation can always edit
    if (isImpersonating) {
      return false;
    }
    
    // Super Admin not impersonating (direct edit)
    if (isSuperAdmin) {
      return false;
    }
    
    // For candidates: only disable if status is "approved" (completed and approved)
    // Allow editing during "waiting" (submitted for review) and null (not submitted)
    return reviewStatus === 'approved';
  }

  return (
    <>
      <div className="d-flex flex-column gap-20 max-w-650px">
        <div className="d-flex gap-20 align-items-center justify-content-between">
          <div className="fs-30 fw-400 text-black">Personal Details</div>
          {isSuperAdmin && (
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
            >
              <RoundedTick />
              Save Changes
            </CustomButton>
          )}
        </div>
        <div className="fs-16 fw-400 text-black">
          Some key information to get your profile started.
        </div>
        <div className="d-flex flex-column gap-20">
          <div className="d-flex flex-column gap-10">
            <div className="d-flex flex-column flex-md-row flex-wrap gap-30 align-items-center">
              <div className="d-flex flex-column order-2 order-md-1 gap-20">
                <ImageUpload
                  type={isSuperAdmin ? "isSuperAdminCandidateEdit" : ""}
                  Apierror={error && error?.errors?.image?.[0]}
                  setUploadValidationError={setUploadValidationError}
                  candidate_uuid={userpersonaldetails?.uuid}
                  previewImage={
                    isSuperAdmin
                      ? userpersonaldetails?.photo ?? ""
                      : user?.photo ?? ""
                  }
                />
              </div>
              <div className="w-100 order-3">
                {(errors || uploadValidationError) && (
                  <ErrorMessage
                    message={
                      (errors.photo as string) ||
                      (uploadValidationError as string)
                    }
                  />
                )}
              </div>
              <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 max-w-250px mobile-w-100 order-1 order-md-2">
                <Warning />
                <div className="fs-13 fw-400 text-black lh-sm">
                  Your profile picture is visible to employers. A high quality
                  image is recommended.
                </div>
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10">
              <div className="flex-fill">
                <TextInput
                  value={values.first_name}
                  field={"first_name"}
                  label={"First Name"}
                  onChange={handleChange}
                  touched={touched.first_name! as boolean}
                  error={errors.first_name as string}
                  placeholder={" " + ""}
                  disabled={!!isConnectIdVerified || shouldFieldBeDisabled()}
                />
              </div>
              <div className="flex-fill">
                <TextInput
                  value={values.last_name}
                  field={"last_name"}
                  label={"Last Name"}
                  onChange={handleChange}
                  touched={touched.last_name! as boolean}
                  error={errors.last_name as string}
                  placeholder={" " + ""}
                  disabled={!!isConnectIdVerified || shouldFieldBeDisabled()}
                />
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10">
              <div className="w-180px mobile-w-100">
                <TextInput
                  value={values.phone_number || ""}
                  field={"phone_number"}
                  label={"Phone Number"}
                  onChange={handleChange}
                  touched={touched.phone_number! as boolean}
                  error={errors.phone_number as string}
                  placeholder={" " + ""}
                  disabled={shouldFieldBeDisabled()}
                />
              </div>
              <div className="flex-fill position-relative">
                <TextInput
                  value={values.address || ""}
                  field={"address"}
                  label={"Home Address"}
                  onChange={handleAddressChange}
                  touched={touched.address! as boolean}
                  error={errors.address as string}
                  placeholder={" " + ""}
                  disabled={!!isConnectIdVerified || shouldFieldBeDisabled()}
                />
                {/* Address Suggestions Dropdown */}
                {addressSuggestions.length > 0 ? (
                  <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                    {addressSuggestions.map((addr, index) => (
                      <div
                        key={index}
                        className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                        onClick={() => handleSelectAddress(addr.description)}
                      >
                        {addr.description}
                      </div>
                    ))}
                  </div>
                )
                : (selectedKeyword.length > 0 && addressSuggestions.length === 0 && values.address === '') ? 
                  <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">No Address Found</div>
                : "" }
              </div>
            </div>
          </div>
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">
              Please upload your resume:
            </div>
            <DocumentUpload
              onFileSelect={handleFileSelect}
              type={"resume"}
              candidate_uuid={isSuperAdmin ? userpersonaldetails?.uuid : null}
              disabled={shouldFieldBeDisabled()}
            />
            {(checkPath && (personaldetails !== null && personaldetails?.resume === null)) ? (
              <ErrorMessage
                className="align-items-center gap-3 lh-1"
                message="Resume is required"
              />
            ) : errors ? <ErrorMessage message={errors.resume as string} /> : (
              ""
            )}
          </div>
          <div className="d-flex flex-column gap-10">
            <div className="theme-check">
              <Checkbox
                className="theme-check-input"
                id="parmenent_employment"
                checked={selectPermanentEmployment}
                onChange={handlePermenentEmploymentCheckBox}
                labelFontSize={"16"}
                label={"I am interested in permanent opportunities as well as casual shifts."}
                themeCheck={true}
                disabled={shouldFieldBeDisabled()}
              />
            </div>
          </div>
          <div className="fs-22 fw-400 text-black">Working Rights</div>
          <div className="fs-16 fw-400 text-black">
            Do you have current working rights in Australia?
          </div>
          <div className="d-flex flex-column gap-10 theme-check">
            <RadioInput
              checked={values.working_rights === "australian_citizen"}
              label={WORKING_RIGHTS_OPTIONS.australian_citizen}
              onChange={handleWorkingRightsOpt}
              value={"australian_citizen"}
              name="working_rights"
              disabled={shouldFieldBeDisabled()}
              themeCheck={true}
            />
            <RadioInput
              checked={values.working_rights === "new_zealand_citizen"}
              label={WORKING_RIGHTS_OPTIONS.new_zealand_citizen}
              onChange={handleWorkingRightsOpt}
              value={"new_zealand_citizen"}
              name="working_rights"
              disabled={shouldFieldBeDisabled()}
              themeCheck={true}
            />
            <RadioInput
              checked={
                values.working_rights === "australian_permanent_resident"
              }
              label={WORKING_RIGHTS_OPTIONS.australian_permanent_resident}
              onChange={handleWorkingRightsOpt}
              value={"australian_permanent_resident"}
              name="working_rights"
              disabled={shouldFieldBeDisabled()}
              themeCheck={true}
            />
            <RadioInput
              checked={values.working_rights === "australian_visa_holder"}
              label={WORKING_RIGHTS_OPTIONS.australian_visa_holder}
              onChange={handleWorkingRightsOpt}
              value={"australian_visa_holder"}
              name="working_rights"
              disabled={shouldFieldBeDisabled()}
              themeCheck={true}
            />
            <RadioInput
              checked={
                values.working_rights === "dont_have_australia_working_rights"
              }
              label={WORKING_RIGHTS_OPTIONS.dont_have_australia_working_rights}
              onChange={handleWorkingRightsOpt}
              value={"dont_have_australia_working_rights"}
              name="working_rights"
              disabled={shouldFieldBeDisabled()}
              themeCheck={true}
            />
            {errors && (
              <ErrorMessage message={errors.working_rights as string} />
            )}
          </div>
          {values.working_rights === "dont_have_australia_working_rights" && (
            <div className="d-flex">
              <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center">
                <Warning />
                <div className="fs-13 fw-400 text-black lh-sm">
                  We require candidates to have current working rights in
                  Australia.&nbsp;
                  <Link
                    className="fw-700 text-black"
                    to="https://immi.homeaffairs.gov.au/visas/working-in-australia"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more
                  </Link>
                </div>
              </div>
            </div>
          )}
          <div className="d-flex flex-column flex-lg-row">
            {!isSuperAdmin && (
              <CustomButton
                type="submit"
                variant="primary"
                disabled={isDisabled}
                onClick={handleSubmit}
                className={`btn-theme ${
                  isDisabled
                    ? "text-grey-medium bg-off-white"
                    : "bg-teal text-black"
                }`}
              >
                Next <RightArrow color={isDisabled ? "#757575" : "#000000"} />
              </CustomButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalDetails;
